/* eslint-disable react/no-danger */
import { useCurrentAccount } from '@application/api';
import { useGetBuyerDraftAnimals } from '@application/api/account/applications/useGetBuyerDraftAnimals';
import { useGetBuyerUnpaidApplications } from '@application/api/account/applications/useGetBuyerUnpaidApplications';
import { useRouter } from '@application/utils';
import type { MtLayoutData } from '@presentation/components';
import { PLACEHOLDER_IMAGE_USER } from '@presentation/constants';
import { useMemo } from 'react';
import { useCheckoutModal } from '../../MtApplicationsBuyerPayModalController/useCheckoutModal';
import { BUYER_APPLICATIONS_ALERT_EXCLUDED_ROUTES } from './constants';
import type { UseBuyerAlertBarParams } from './types';

export const useBuyerAlertBar = (params: UseBuyerAlertBarParams) => {
  const { currentAccountType } = params;

  const { translate, infoFromCurrentPath, routeLocationDescriptor } = useRouter();

  const { currentAccount } = useCurrentAccount();

  const { unpaidApplications } = useGetBuyerUnpaidApplications();
  const { draftAnimals } = useGetBuyerDraftAnimals();
  const unpaidApplication = unpaidApplications?.[0];

  const {
    checkoutApplicationClickHandler,
    checkoutApplicationLoading,
    checkoutApplicationModalProps,
  } = useCheckoutModal(unpaidApplication?.id, {
    buyerImage: currentAccount?.imgs?.[0]?.small || PLACEHOLDER_IMAGE_USER,
    breederImage: unpaidApplication?.dogBreeder?.profileImg || PLACEHOLDER_IMAGE_USER,
  });

  const alertBar = useMemo((): MtLayoutData['alertBar'] => {
    if (currentAccountType === 'breeder') return;
    // Return when in details
    if (infoFromCurrentPath.route === 'mtApplications' && infoFromCurrentPath.params.length > 0)
      return;
    if (BUYER_APPLICATIONS_ALERT_EXCLUDED_ROUTES.includes(infoFromCurrentPath.route)) return;

    // Priority matters here
    if (draftAnimals.length > 0) {
      return {
        title: translate('mt.buyer.animalsAlertBar.title'),
        content: translate('mt.buyer.animalsAlertBar.text'),
        linkText: translate('common.buttons.continue'),
        link: routeLocationDescriptor('mtAnimals', { params: [draftAnimals[0].id.toString()] }),
        theme: 'success',
      };
    }

    if (
      currentAccount?.accountRemainingDaysForFreeApplications &&
      currentAccount.accountRemainingDaysForFreeApplications > 0
    ) {
      return {
        title: (
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: translate('mt.buyer.paymentAlertBar.title').replace(
                '$daysLeft',
                currentAccount.accountRemainingDaysForFreeApplications.toString(),
              ),
            }}
          />
        ),
        content: translate('mt.buyer.paymentAlertBar.text').replace(
          '$daysLeft',
          currentAccount?.accountRemainingDaysForFreeApplications.toString(),
        ),
        theme: 'success',
      };
    }

    if (unpaidApplications.length > 0) {
      return {
        title: translate('mt.buyer.applicationAlertBar.title'),
        content: translate('mt.buyer.applicationAlertBar.text'),
        linkText: translate('common.buttons.pay'),
        loading: checkoutApplicationLoading,
        theme: 'success',
        onClick: checkoutApplicationClickHandler,
      };
    }
  }, [
    currentAccountType,
    infoFromCurrentPath.route,
    infoFromCurrentPath.params.length,
    draftAnimals,
    currentAccount?.accountRemainingDaysForFreeApplications,
    unpaidApplications.length,
    translate,
    routeLocationDescriptor,
    checkoutApplicationLoading,
    checkoutApplicationClickHandler,
  ]);

  return {
    alertBar,
    checkoutApplicationModalProps,
  };
};
